import React, { useContext, useState, type FC } from "react";

import clsx from "clsx";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { gql } from "@apollo/client";
import { LoadingSlide } from "@components/title-slide-view";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { bugsnagClient } from "@internal/analytics";
import {
  type DomainCollectionQuery,
  type SetDomainIfNotTaken,
  type SetDomainIfNotTakenVariables,
} from "@north-beam/nb-client-side";
import { PROFILE_QUERY } from "@pages/settings/profile";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { verifyDomain } from "@utils/domain-verification";

import { SideNavContext } from "..";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { ReadDocumentationButton } from "./components/info-panel";
import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";

export const DOMAIN_COLLECTION_QUERY = gql`
  query DomainCollectionQueryV2 {
    me {
      id
      domainName
    }
  }
`;

export const SET_DOMAIN_IF_NOT_TAKEN = gql`
  mutation SetDomainIfNotTakenV2($domainName: String!) {
    setDomainIfNotTaken(domainName: $domainName)
  }
`;

interface UpdateDomainNameProps {
  readonly value: string;
  readonly domainName: string | null;
  readonly updating: boolean;
  readonly error: string | null;
  readonly onChange: (value: string) => void;
  readonly onSubmitClicked: () => void;
}

const UpdateDomainName = ({
  value,
  domainName,
  updating,
  error,
  onChange,
  onSubmitClicked,
}: UpdateDomainNameProps) => (
  <div className="max-w-[440px] w-full">
    <div className="control-label">Domain name:</div>
    <div className="input-group">
      {domainName === null || domainName === "" ? (
        <>
          <div className="input-group-prepend">
            <div className="input-group-text">https://</div>
          </div>
          <input
            type="text"
            disabled={updating}
            className="form-control"
            value={value}
            onChange={(event) => {
              onChange(event.target.value.toLowerCase());
            }}
          />
          <PrimaryButton
            loading={updating}
            onClick={() => onSubmitClicked()}
            disabled={!value || !!verifyDomain(value) || updating}
            className="ml-4 uppercase rounded-[20px] text-sm font-semibold"
          >
            Submit Domain
          </PrimaryButton>
        </>
      ) : (
        <div className="relative w-[440px]">
          <input
            type="text"
            disabled={true}
            className="text-interstellar+0 text-sm form-control border border-gray-1 bg-alien-2 rounded"
            value={`https://${domainName}`}
          />
          <div className="absolute top-[50%] translate-y-[-50%] right-[12px] flex justify-center items-center gap-1">
            <i className="fa-solid fa-check text-alien+0"></i>
            <div className="text-alien+0 text-sm">Valid Domain</div>
          </div>
        </div>
      )}
    </div>
    {error && <p style={{ color: "red" }}>{error}</p>}
  </div>
);

export const DomainCollection: FC<StepChildProps> = ({
  nextStep,
  previousStep,
  isLast,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const context = React.useContext(SideNavContext);
  const { setSubNavItems } = useContext(SideNavContext);

  if (context?.subNavItems?.length > 0) {
    setSubNavItems([]);
  }

  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
  });
  const [unsavedDomainName, setUnsavedDomainName] = useState<string>("");
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  const [isDomainAck, setIsDomainAck] = useState(false);
  const [setDomainNameError, setSetDomainNameError] = useState<string | null>(
    "",
  );

  const { data, error: domainCollectionError } =
    useNorthbeamQuery<DomainCollectionQuery>(DOMAIN_COLLECTION_QUERY, {
      fetchPolicy: "no-cache",
      onError: (e) => {
        setSetDomainNameError(
          "There was an error updating the domain name. Please contact support if the issue persists",
        );
        bugsnagClient.notify({
          name: "There was an error fetching the user's domain name during onboarding.",
          message: JSON.stringify(e),
        });
      },
    });

  const [setDomain, { loading: setDomainLoading }] = useNorthbeamMutation<
    SetDomainIfNotTaken,
    SetDomainIfNotTakenVariables
  >(SET_DOMAIN_IF_NOT_TAKEN, {
    refetchQueries: [
      { query: DOMAIN_COLLECTION_QUERY },
      { query: PROFILE_QUERY },
    ],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      if (result.setDomainIfNotTaken === false) {
        setSetDomainNameError(
          "This domain is already taken. If you are trying to join an existing account, " +
            "please reach out to your account owner.",
        );
      } else {
        setSearchParams({
          step: Steps.DNS_SETUP,
        });
        nextStep?.();
      }
    },
    onError: (e) => {
      bugsnagClient.notify({
        name: "There was an error updating the user's domain name during onboarding.",
        message: JSON.stringify(e),
      });
      setSetDomainNameError("There was an error updating the domain name.");
    },
  });
  const { stepDictionary } = useOnboardingStep();

  return (
    <StepContainer
      title={`${stepDictionary.domain.index}. Enter Domain Name`}
      isLast={isLast}
      onMoveForwardButtonClicked={async () => {
        if (data !== undefined) {
          if (data.me.domainName) {
            setSearchParams({
              step: Steps.DNS_SETUP,
            });
            nextStep?.();
          } else {
            setSetDomainNameError(null);
            const error = verifyDomain(unsavedDomainName);
            if (error) {
              setSetDomainNameError(error);
            } else {
              setConfirmModalOpen(true);
            }
          }
        }
      }}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.CURRENCY,
        });
        previousStep && previousStep();
      }}
      knowledgeBaseLink="https://docs.northbeam.io/docs/add-domain"
      knowledgeBaseText="Add Domain"
    >
      <Step
        nextStepButtonText={`${stepDictionary["dns-setup"].index}. Configure DNS`}
        loadingNextStep={setDomainLoading}
        nextStep={async () => {
          if (data !== undefined) {
            if (data.me.domainName) {
              setSearchParams({
                step: Steps.DNS_SETUP,
              });
              nextStep?.();
            } else {
              setSetDomainNameError(null);
              const error = verifyDomain(unsavedDomainName);
              if (error) {
                setSetDomainNameError(error);
                setIsConfirmModalOpen(true);
              } else {
                setConfirmModalOpen(true);
              }
            }
          }
        }}
      >
        {data !== undefined ? (
          <>
            <div className="w-[922px] px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex">
              <div className="self-stretch text-2xl leading-loose">
                Please tell us the domain name to track
              </div>
              <UpdateDomainName
                value={unsavedDomainName}
                domainName={data.me.domainName}
                onChange={(v) => {
                  setSetDomainNameError(null);
                  const error = verifyDomain(v);
                  if (error) {
                    setSetDomainNameError(error);
                  }
                  setUnsavedDomainName(v);
                }}
                error={setDomainNameError}
                updating={setDomainLoading}
                onSubmitClicked={() => setConfirmModalOpen(true)}
              />
              {data.me.domainName && (
                <div className="bg-sun-2 border border-gray-0 w-full flex rounded items-center px-4 py-6">
                  <i className="fa-solid fa-triangle-exclamation text-[#FFB300] mr-4"></i>
                  <div className="text-lg font-semibold pr-1">
                    If you submitted the wrong domain name
                  </div>
                  <a
                    href="mailto:success@northbeam.io"
                    target="_blank"
                    className="pr-1 font-semibold text-lg"
                    rel="noreferrer"
                  >
                    contact us
                  </a>
                  <div>immediately</div>
                </div>
              )}
              <div className="p-6 bg-cosmic-5 rounded-lg border border-gray-0 flex-col justify-end items-start gap-2 inline-flex">
                <div className="self-stretch justify-start items-center gap-2 inline-flex">
                  <div className="w-[18px] h-[18px] relative">
                    <div className="w-[18px] h-[18px] bg-cosmic rounded-full border border-black flex justify-center items-center">
                      <i
                        className="fa-solid fa-question fa-xs"
                        style={{ color: "#ffffff" }}
                      ></i>
                    </div>
                  </div>
                  <div className="grow shrink basis-0 text-lg font-semibold leading-normal">
                    Have storefronts on multiple domains or sub-domains?
                  </div>
                </div>
                <div className="self-stretch">
                  <span className="text-sm font-normal leading-tight">
                    A common example is for{" "}
                  </span>
                  <span className="text-sm font-normal leading-tight">
                    international stores
                  </span>
                  <span className="text-sm font-normal leading-tight">
                    , in some cases it makes sense to create separate Northbeam
                    accounts for each one. Often the checkout flow of each store
                    (shared or separate) will determine whether it makes most
                    sense to segment reporting into separate accounts.{" "}
                  </span>
                </div>
                <ReadDocumentationButton docUrl="https://docs.northbeam.io/docs/confirm-setup-requirements" />
              </div>
            </div>
          </>
        ) : domainCollectionError ? (
          <p style={{ color: "red" }}>
            There was an error retrieving information about your account.
          </p>
        ) : (
          <div className="h-[600px]">
            <LoadingSlide />
          </div>
        )}
        <Modal
          isOpen={confirmModalOpen}
          titleContent={
            <div className="font-bold">
              Submit "
              {data?.me?.domainName === "" || data?.me?.domainName === null
                ? unsavedDomainName
                : data?.me?.domainName}
              " as your business's domain name?
            </div>
          }
          onClose={() => {
            if (setDomainLoading) {
              return;
            }
            setConfirmModalOpen(false);
          }}
        >
          <div className="flex flex-col justify-evenly py-8">
            <div>
              <ConfirmCheckbox
                onChange={setIsDomainAck}
                inputId="ack-domain"
                disabled={setDomainLoading}
              >
                I understand that the submitted domain name cannot be changed.
              </ConfirmCheckbox>
            </div>
          </div>
          <div className="w-full h-11 self-stretch justify-end items-end gap-2 inline-flex">
            <SecondaryButton
              variant="tertiary"
              className="font-semibold uppercase"
              disabled={setDomainLoading}
              onClick={() => {
                setConfirmModalOpen(false);
                setIsDomainAck(false);
              }}
            >
              No, Exit
            </SecondaryButton>
            <PrimaryButton
              loading={setDomainLoading}
              className="font-semibold uppercase"
              isDisabled={!isDomainAck}
              onClick={async () => {
                await setDomain({
                  variables: { domainName: unsavedDomainName },
                });
                setConfirmModalOpen(false);
                setIsDomainAck(false);
              }}
            >
              Yes, Change Domain name
            </PrimaryButton>
          </div>
        </Modal>
        <Modal
          isOpen={isConfirmModalOpen}
          titleContent={
            <div className="font-bold">
              Complete following steps to mark this step ‘complete’
            </div>
          }
          onClose={() => {
            setIsConfirmModalOpen(false);
          }}
        >
          <div className="h-[320px] overflow-y-auto">
            <div
              className={clsx(
                "w-full py-5 flex items-center justify-between border-b border-[#D9D9D9]",
              )}
            >
              <span className="text-md font-semibold leading-7">
                Domain name
              </span>
              <div className="flex flex-col gap-0.5 rounded-3xl px-5 py-1.5 bg-interstellar-3 justify-start items-center">
                <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide">
                  Incomplete
                </span>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsConfirmModalOpen(false);
                  }}
                  className="text-base leading-none font-semibold text-md no-underline"
                >
                  Go to step
                </a>
              </div>
            </div>
          </div>
          <div className="w-full justify-end flex">
            <SecondaryButton
              variant="tertiary"
              className="font-semibold uppercase"
              onClick={() => {
                setSearchParams({
                  step: Steps.DNS_SETUP,
                });
                nextStep && nextStep();
              }}
            >
              Skip for now
            </SecondaryButton>
          </div>
        </Modal>
      </Step>
    </StepContainer>
  );
};

const ConfirmCheckbox = ({
  inputId,
  onChange,
  disabled,
  ...props
}: {
  inputId: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}) => {
  const [checked, setChecked] = useState(false);
  return (
    <div
      className={clsx(
        "h-16 self-stretch p-6 rounded-lg border justify-between items-center inline-flex",
        "bg-cosmic-5",
      )}
    >
      <div className="justify-start items-center gap-2 inline-flex">
        <input
          id={inputId}
          type="checkbox"
          className="pb-0 mb-0"
          onChange={(e) => {
            setChecked(e.target.checked);
            onChange(e.target.checked);
          }}
          checked={checked}
          disabled={disabled}
        />
        <label htmlFor={inputId} className="mb-0 pb-0 text-lg">
          {props.children}
        </label>
      </div>
    </div>
  );
};
